import { useSubdomainOrg } from "@/hooks/useSubdomainOrg";
import { useAuth } from "@/contexts/AuthContext";
import SubdomainLogin from "@/pages/SubdomainLogin";
import { Loader2, AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Navigate } from "react-router-dom";

interface SubdomainRouterProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that handles subdomain-based routing
 * If on a subdomain, shows org-specific login/content
 * Otherwise, falls through to path-based routing
 */
export function SubdomainRouter({ children }: SubdomainRouterProps) {
  const { organization, loading, error, isSubdomainMode } = useSubdomainOrg();
  const { user, loading: authLoading } = useAuth();
  const { setOrganizationBySlug } = useOrganization();

  // Set organization context when detected from subdomain
  useEffect(() => {
    if (organization && user) {
      setOrganizationBySlug(organization.slug);
    }
  }, [organization, user, setOrganizationBySlug]);

  // Still detecting subdomain
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not on subdomain - use regular path-based routing
  if (!isSubdomainMode) {
    return <>{children}</>;
  }

  // Subdomain detected but org not found
  if (error || !organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Organization Not Found</h1>
          <p className="text-muted-foreground">
            This subdomain is not configured. Please check the URL.
          </p>
        </div>
      </div>
    );
  }

  // Still checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Subdomain mode: not logged in - show org-specific login
  if (!user) {
    return <SubdomainLogin organization={organization} />;
  }

  // Logged in on subdomain - redirect to org dashboard
  return <Navigate to={`/${organization.slug}`} replace />;
}
